'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-recycle',
    titleEs: 'Retiro y Recolección de RAEE',
    titleFr: 'Collecte et Enlèvement des DEEE',
    descriptionEs:
      'Servicio de retiro en sitio de residuos de aparatos eléctricos y electrónicos (RAEE). Coordinamos la logística completa para que el proceso sea simple y sin interrupciones.',
    descriptionFr:
      'Service d\'enlèvement sur site des déchets d\'équipements électriques et électroniques (DEEE). Nous coordonnons la logistique complète pour que le processus soit simple et sans interruption.',
  },
  {
    icon: 'fas fa-certificate',
    titleEs: 'Certificado de Destrucción',
    titleFr: 'Certificat de Destruction',
    descriptionEs:
      'Emitimos certificados oficiales de destrucción y disposición final de equipos, garantizando el cumplimiento normativo y la trazabilidad de cada activo dado de baja.',
    descriptionFr:
      'Nous émettons des certificats officiels de destruction et d\'élimination finale des équipements, garantissant la conformité réglementaire et la traçabilité de chaque actif mis hors service.',
  },
  {
    icon: 'fas fa-hdd',
    titleEs: 'Borrado Seguro de Datos',
    titleFr: 'Effacement Sécurisé des Données',
    descriptionEs:
      'Eliminación certificada de datos en discos duros, SSDs y dispositivos de almacenamiento mediante estándares internacionales (DoD 5220.22-M, NIST 800-88) antes de su reciclaje.',
    descriptionFr:
      'Suppression certifiée des données sur disques durs, SSD et dispositifs de stockage selon les normes internationales (DoD 5220.22-M, NIST 800-88) avant leur recyclage.',
  },
  {
    icon: 'fas fa-leaf',
    titleEs: 'Reciclaje Responsable',
    titleFr: 'Recyclage Responsable',
    descriptionEs:
      'Procesamiento de equipos a través de gestores autorizados, asegurando la separación y valorización de materiales conforme a la Ley REP y normativas medioambientales vigentes.',
    descriptionFr:
      'Traitement des équipements via des gestionnaires agréés, assurant la séparation et la valorisation des matériaux conformément à la législation environnementale en vigueur.',
  },
  {
    icon: 'fas fa-clipboard-list',
    titleEs: 'Inventario y Auditoría de Activos',
    titleFr: 'Inventaire et Audit des Actifs',
    descriptionEs:
      'Levantamiento detallado del inventario de equipos a reciclar, con clasificación por tipo, marca, modelo y estado, facilitando la gestión contable y de activos fijos.',
    descriptionFr:
      'Inventaire détaillé des équipements à recycler, avec classification par type, marque, modèle et état, facilitant la gestion comptable et des immobilisations.',
  },
  {
    icon: 'fas fa-handshake',
    titleEs: 'Programas Corporativos de Reciclaje',
    titleFr: 'Programmes Corporatifs de Recyclage',
    descriptionEs:
      'Diseño de programas continuos de gestión de residuos electrónicos para empresas e instituciones, con reportes periódicos de impacto ambiental y cumplimiento normativo.',
    descriptionFr:
      'Conception de programmes continus de gestion des déchets électroniques pour les entreprises et institutions, avec des rapports périodiques d\'impact environnemental et de conformité réglementaire.',
  },
];

const sectors = [
  { icon: 'fas fa-building', labelEs: 'Gobierno', labelFr: 'Gouvernement' },
  { icon: 'fas fa-university', labelEs: 'Educación', labelFr: 'Éducation' },
  { icon: 'fas fa-hospital', labelEs: 'Salud', labelFr: 'Santé' },
  { icon: 'fas fa-industry', labelEs: 'Industria', labelFr: 'Industrie' },
  { icon: 'fas fa-store', labelEs: 'Retail', labelFr: 'Commerce' },
  { icon: 'fas fa-leaf', labelEs: 'Sustentabilidad', labelFr: 'Durabilité' },
];

export default function ReciclajeElectronicoPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" /></div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" /></div>
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Recyclage Électronique' : 'Reciclaje Electrónico'}</span>
          </nav>
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fas fa-recycle" /><span>{isFr ? 'GESTION RESPONSABLE DES DEEE' : 'GESTIÓN RESPONSABLE DE RAEE'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Recyclage' : 'Reciclaje'}<br /><span style={{ color: '#1AEF0F' }}>{isFr ? 'Électronique' : 'Electrónico'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr ? 'Nous gérons le cycle de vie final de vos équipements technologiques avec responsabilité environnementale, sécurité des données et conformité réglementaire. Parce que bien éliminer fait aussi partie de la transformation numérique.' : 'Gestionamos el ciclo de vida final de tus equipos tecnológicos con responsabilidad ambiental, seguridad de datos y cumplimiento normativo. Porque desechar bien también es parte de la transformación digital.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center overflow-hidden" style={{ border: '1px solid #1AEF0F', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', color: '#1AEF0F', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">{isFr ? 'Demander un Enlèvement' : 'Solicitar Retiro'}</span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#1AEF0F', zIndex: 0 }} />
                </a>
                <Link href="/#servicios" className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors" style={{ fontSize: '15px', padding: '14px 0' }}>
                  <i className="fas fa-arrow-left text-sm" />{isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>
            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: '+10 ton', labelEs: 'RAEE gestionados', labelFr: 'DEEE gérés' },
                { value: '100%', labelEs: 'Datos eliminados', labelFr: 'Données effacées' },
                { value: 'Ley REP', labelEs: 'Cumplimiento normativo', labelFr: 'Conformité légale' },
                { value: 'Cert.', labelEs: 'Destrucción certificada', labelFr: 'Destruction certifiée' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '24px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>{stat?.value}</div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{isFr ? stat?.labelFr : stat?.labelEs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.capabilitiesTitle}</h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>{isFr ? 'Un service intégral de gestion des déchets électroniques qui protège vos données, respecte la réglementation en vigueur et contribue à un avenir plus durable.' : 'Un servicio integral de gestión de residuos electrónicos que protege tus datos, cumple la normativa vigente y contribuye a un futuro más sostenible.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>{isFr ? cap?.titleFr : cap?.titleEs}</h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>{isFr ? cap?.descriptionFr : cap?.descriptionEs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#f5f5f5' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-black mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.sectorsTitle}</h2>
          <div className="flex flex-wrap gap-4">
            {sectors?.map((sec, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4 bg-white" style={{ border: '1px solid #E2E2E2' }}>
                <i className={`${sec?.icon} text-[#1AEF0F]`} />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>{isFr ? sec?.labelFr : sec?.labelEs}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-black font-bold m-0" style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}>
            {isFr ? 'Vous avez des équipements technologiques à recycler de manière responsable ?' : '¿Tienes equipos tecnológicos para reciclar de forma responsable?'}
          </p>
          <Link href="/contact" className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0" style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{s?.ctaBtnContact}</span>
            <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
